import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { useKycStatus } from '@shared/hooks/use-associate';
import type { KycStatus } from '@shared/types/api';

// Prominent status card for the Associate. Renders on the Transactions page
// (and anywhere else the Associate needs to see where they stand at a glance).
// `KycGate` still blocks NOT_SUBMITTED / REJECTED from the rest of the UI;
// this card is a compact summary that survives after the gate is dismissed
// and keeps surfacing the state.

type Tone = 'neutral' | 'warn' | 'success' | 'danger';

const TONES: Record<Tone, string> = {
  neutral: 'border-gray-200 bg-gray-50 text-gray-800',
  warn: 'border-amber-200 bg-amber-50 text-amber-900',
  success: 'border-brand-200 bg-brand-50/70 text-brand-900',
  danger: 'border-red-200 bg-red-50 text-red-900',
};

const DESCRIPTION: Record<KycStatus, { tone: Tone; headline: string; body: string }> = {
  NOT_SUBMITTED: {
    tone: 'warn',
    headline: 'KYC not submitted',
    body: 'Submit your Aadhaar, PAN, and bank details so admin can verify you. Until then you won\u2019t be assigned any client requests.',
  },
  PENDING: {
    tone: 'warn',
    headline: 'KYC pending admin review',
    body: 'Your details are with admin. We\u2019ll notify you once approved. Assignment is paused until then.',
  },
  VERIFIED: {
    tone: 'success',
    headline: 'KYC verified',
    body: 'You\u2019re fully verified — admin can assign client transactions to you.',
  },
  REJECTED: {
    tone: 'danger',
    headline: 'KYC rejected',
    body: 'Admin needs you to resubmit. The reason they sent back is shown below.',
  },
};

export function KycStatusCard() {
  const { data: status } = useKycStatus();
  if (!status) return null;

  const info = DESCRIPTION[status.status];

  return (
    <section className={cn('rounded-xl border p-5', TONES[info.tone])}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold">{info.headline}</h2>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
                info.tone === 'success'
                  ? 'bg-brand-700 text-white'
                  : info.tone === 'danger'
                    ? 'bg-red-600 text-white'
                    : info.tone === 'warn'
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-700 text-white',
              )}
            >
              {status.status}
            </span>
          </div>
          <p className="mt-1 text-sm">{info.body}</p>

          {status.status === 'REJECTED' && status.rejectionReason ? (
            <p className="mt-2 rounded-md border border-red-200 bg-white/60 px-3 py-2 text-xs text-red-800">
              <span className="font-medium">Admin note:</span> {status.rejectionReason}
            </p>
          ) : null}

          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px] opacity-80">
            {status.submittedAt ? (
              <>
                <dt>Submitted</dt>
                <dd>{new Date(status.submittedAt).toLocaleString()}</dd>
              </>
            ) : null}
            {status.verifiedAt ? (
              <>
                <dt>Verified</dt>
                <dd>{new Date(status.verifiedAt).toLocaleString()}</dd>
              </>
            ) : null}
          </dl>
        </div>
        {status.status !== 'VERIFIED' && status.status !== 'PENDING' ? (
          <Link to="/associate/kyc">
            <Button className="h-8 px-3 py-0.5 text-xs">
              {status.status === 'REJECTED' ? 'Resubmit KYC' : 'Complete KYC'}
            </Button>
          </Link>
        ) : null}
      </div>
    </section>
  );
}
